import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Input,
  VStack,
  Text,
  useColorModeValue,
  IconButton,
  HStack,
  Tooltip,
} from '@chakra-ui/react';
import { CheckIcon, CloseIcon } from '@chakra-ui/icons';

interface SelectionCommentMenuProps {
  onComment: (selectedText: string, comment: string, startIndex: number, endIndex: number) => void;
  isProcessing: boolean;
}

const SelectionCommentMenu: React.FC<SelectionCommentMenuProps> = ({ onComment, isProcessing }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [selectedText, setSelectedText] = useState('');
  const [comment, setComment] = useState('');
  const [selectionRange, setSelectionRange] = useState({ start: 0, end: 0 });
  const menuRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // カラーモード対応
  const menuBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const shadowColor = useColorModeValue('rgba(0, 0, 0, 0.1)', 'rgba(0, 0, 0, 0.3)');

  useEffect(() => {
    const handleTextareaSelection = (e: Event) => {
      const textarea = e.target as HTMLTextAreaElement;
      if (!textarea || textarea.tagName !== 'TEXTAREA') return;
      
      // 入力エリアのTextareaのみを対象にする
      const isInputTextarea = textarea.hasAttribute('data-testid') && 
                              textarea.getAttribute('data-testid') === 'input-textarea';
      
      if (!isInputTextarea) {
        console.log('Not input textarea, skipping');
        return;
      }

      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      
      console.log('Selection detected:', { start, end, textLength: textarea.value.length });
      
      if (start === end || start >= end) {
        setIsVisible(false);
        return;
      }

      const selectedText = textarea.value.substring(start, end).trim();
      console.log('Selected text:', selectedText);
      
      if (selectedText.length === 0) {
        setIsVisible(false);
        return;
      }

      // TextareaのBoundingRectを取得
      const rect = textarea.getBoundingClientRect();
      
      // 選択位置を概算（完全ではないが、実用的な位置計算）
      const textBeforeSelection = textarea.value.substring(0, start);
      const lines = textBeforeSelection.split('\n');
      const currentLine = lines.length - 1;
      const charInLine = lines[lines.length - 1].length;
      
      // 概算位置（より正確には文字の位置計算が必要だが、簡易実装）
      const x = rect.left + (charInLine * 8) + 20; // 文字幅の概算
      const y = rect.top + (currentLine * 20) + 20; // 行高の概算

      setSelectedText(selectedText);
      setSelectionRange({ start, end });
      setPosition({ x, y });
      setIsVisible(true);
      setComment('');
      
      console.log('Menu positioned at:', { x, y });
    };

    const handleMouseUp = (e: MouseEvent) => {
      setTimeout(() => handleTextareaSelection(e), 50);
    };

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsVisible(false);
      }
    };

    // より確実な検出のため、複数のイベントを監視
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('selectionchange', () => {
      const activeElement = document.activeElement;
      if (activeElement && activeElement.tagName === 'TEXTAREA') {
        handleTextareaSelection({ target: activeElement } as unknown as Event);
      }
    });
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('selectionchange', () => {});
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isVisible && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isVisible]);

  const handleSubmit = () => {
    if (comment.trim() && selectedText) {
      onComment(selectedText, comment, selectionRange.start, selectionRange.end);
      setIsVisible(false);
      setComment('');
      // 選択を解除
      window.getSelection()?.removeAllRanges();
    }
  };

  const handleCancel = () => {
    setIsVisible(false);
    setComment('');
    window.getSelection()?.removeAllRanges();
  };

  if (!isVisible) return null;

  return (
    <Box
      ref={menuRef}
      position="fixed"
      left={`${position.x}px`}
      top={`${position.y}px`}
      transform="translate(-50%, -100%)"
      zIndex={1000}
      bg={menuBg}
      border="1px solid"
      borderColor={borderColor}
      borderRadius="md"
      shadow={`0 2px 8px ${shadowColor}`}
      p={3}
      minWidth="250px"
      maxWidth="350px"
    >
      <VStack spacing={2} align="stretch">
        <Text fontSize="xs" fontWeight="bold" color="gray.600">
          選択部分へのコメント:
        </Text>
        <Text fontSize="xs" noOfLines={2} color="gray.500">
          "{selectedText}"
        </Text>
        <HStack>
          <Input
            ref={inputRef}
            size="sm"
            placeholder="例: もっと簡潔に"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSubmit();
              } else if (e.key === 'Escape') {
                handleCancel();
              }
            }}
            isDisabled={isProcessing}
          />
          <Tooltip label="適用 (Enter)">
            <IconButton
              aria-label="適用"
              icon={<CheckIcon />}
              size="sm"
              colorScheme="blue"
              onClick={handleSubmit}
              isDisabled={!comment.trim() || isProcessing}
            />
          </Tooltip>
          <Tooltip label="キャンセル (Esc)">
            <IconButton
              aria-label="キャンセル"
              icon={<CloseIcon />}
              size="sm"
              variant="ghost"
              onClick={handleCancel}
              isDisabled={isProcessing}
            />
          </Tooltip>
        </HStack>
      </VStack>
    </Box>
  );
};

export default SelectionCommentMenu;